// @ts-strict
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { LMActionPickerService } from "./lm-action-picker.service";
import { Subject, takeUntil } from "rxjs";
import { SohoDataGridComponent } from "ids-enterprise-ng";
import { LMFieldPickerComponent, LMFieldPickerService } from "../field-picker";
import { LMContextService } from "@services/lm-context.service";
import { AppConstants } from "@app/utils";

@Component({
  standalone: false,
  selector: "lm-action-picker",
  templateUrl: "./lm-action-picker.component.html",
  styleUrl: "./lm-action-picker.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LMActionPickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("actionPickerDataGridComponent", { static: false }) private dg!: SohoDataGridComponent;

  private destroyer$ = new Subject<void>();

  // will be used for runtime column title depending on type
  private columnTypeId: "type" | typeof LMFieldPickerComponent.LABEL_COLUMN_ID = "type";

  public gridOptions!: SohoDataGridOptions;
  public selectedActions: LMActionSpec[] | null = null;

  constructor(
    private readonly service: LMActionPickerService,
    private readonly lmFieldPickerService: LMFieldPickerService,
    private readonly lmContextService: LMContextService
  ) {}

  ngOnInit(): void {
    this.initializeGrid();
  }

  ngAfterViewInit(): void {
    this.service.actionSpecs$
      .pipe(takeUntil(this.destroyer$))
      .subscribe((actionSpecs: LMActionSpec[]) => {
        if (this.dg) {
          this.dg.dataset = actionSpecs;
          if (actionSpecs.length) {
            this.dg.showColumn(this.columnTypeId);
            this.dg.setSortColumn("name", true);
            this.dg.clearFilter();
            this.dg.keywordSearch("");
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  onRowSelected(event: SohoDataGridSelectedEvent) {
    if (!event) return;

    if (Array.isArray(event.rows)) {
      const selectedActions = event.rows.map(row => row?.data) as LMActionSpec[];
      this.selectedActions = selectedActions;
    }
  }

  public getActionSpecs(
    businessClass: string,
    listName: string,
    type?: ReadonlyArray<LMActionPickerActionType>,
    excludedActions?: ReadonlyArray<string>
  ) {
    if (type.includes("navigation")) {
      this.columnTypeId = LMFieldPickerComponent.LABEL_COLUMN_ID;
    }

    this.service.getActionSpecs(businessClass, listName, type, excludedActions);
  }

  private onColumnClicked(
    event: Event,
    args: SohoDataGridColumnClickData[],
    columnId: string
  ) {
    if (args?.length > 0) {
      const uiFieldSpec = args[0].item;

      if (LMFieldPickerComponent.LABEL_COLUMN_ID === columnId) {
        this.lmFieldPickerService.openFieldHelpDialog(uiFieldSpec);
      }
    }
  }

  private initializeGrid(): void {
    const isConfigurator = this.lmContextService.isConfigFeatureEnabled(AppConstants.FEATURE_Configuration);

    this.gridOptions = {
      selectable: "multiple",
      paging: false,
      rowReorder: false,
      editable: false,
      rowHeight: "short",
      isList: true,
      showDirty: false,
      enableTooltips: true,
      stickyHeader: false,
      headerBackgroundColor: "light",
      clickToSelect: true,
      toolbar: {
        title: "",
        results: true,
        actions: true,
        keywordFilter: true,
        contextualToolbar: true,
      },
      columns: [
        {
          id: "selectionCheckbox",
          sortable: false,
          resizable: false,
          formatter: Soho.Formatters.SelectionCheckbox,
          align: "center",
        },
        {
          id: "name",
          name: Soho.Locale.translate("FieldPicker_FieldNameLabel"),
          sortable: false,
          field: "name",
          formatter: Soho.Formatters.Text,
          textOverflow: "ellipsis",
        },
        {
          id: "label",
          name: Soho.Locale.translate("FieldPicker_FieldLabelLabel"),
          sortable: false,
          field: "label",
          formatter: Soho.Formatters.Text,
          textOverflow: "ellipsis",
        },
        {
          id: "type",
          name: Soho.Locale.translate("Type"),
          sortable: false,
          field: "actionSpecType",
          formatter: Soho.Formatters.Text,
          textOverflow: "ellipsis",
          hidden: true,
        },
        {
          id: LMFieldPickerComponent.LABEL_COLUMN_ID,
          name: Soho.Locale.translate("NavigationTarget"),
          sortable: false,
          field: "actionSpecType",
          formatter: isConfigurator ? Soho.Formatters.Hyperlink : Soho.Formatters.Text,
          textOverflow: "ellipsis",
          hidden: true,
          click: (event: MouseEvent, args: SohoDataGridColumnClickData[]) =>
            this.onColumnClicked(
              event,
              args,
              LMFieldPickerComponent.LABEL_COLUMN_ID
            ),
        },
      ],
      emptyMessage: {
        title: Soho.Locale.translate("Actions"),
        info: Soho.Locale.translate("ListPersonalizationEmptyMessage"),
      },
    };
  }
}
